import React, { createContext, useContext, useEffect, useState } from 'react'
import HelperCache, { HelperCacheFetchConstants } from 'utils/HelperCache'
import HelperSettingNotifications from 'utils/helper-setting-notifications'
import GraphqlBuilder from 'utils/request/graphql/builder'
import _ from 'lodash'
import MutationEmployeeLangauge from 'graphql/mutations/MutationEmployeeLangauge'
import HelperLocale from 'utils/helper-locale'
import { deviceInformation } from 'utils/helper-device-information'
import MutationEmployeeVersion from 'graphql/mutations/MutationEmployeeVersion'
import QueryFilters from 'graphql/queries/QueryFilters'
import { setData } from 'services/redux/data/actions'
import { dataConstants } from 'services/redux/data/constants'
import { useDispatch, useSelector } from 'react-redux'
import selectEmployeeLanguage from 'services/redux/data/selectors/selectEmployeeLanguage'
import HelperLocaleSettings from 'utils/helper/HelperLocaleSettings'
import CacheStorage from 'utils/cache/CacheStorage'
import { getInitialURL } from 'expo-linking'
import HelperVersion from 'utils/helper-version'
import { appRestart } from 'utils/helper-restart'
import i18n from 'utils/i18n'
import GraphqlGroupedBuilder from 'utils/request/graphql/groupedBuilder'
import { useSplash } from 'components/provider/SplashProvider'
import { useConfigProvider } from 'components/provider/ConfigProvider'

export const CacheProviderContext = createContext( null );

export const useCache  = () => {
	return useContext( CacheProviderContext );
}

const CacheProvider = ({ onError, children }) => {
	
	const dispatch = useDispatch()
	const _employeeLanguage = useSelector( selectEmployeeLanguage )
	const { setProgressBarPercentage } = useSplash()
	const { customerId } = useConfigProvider()
	
	const [ loading, setLoading ] = useState( true )

	const validateCacheVersion = async () => {
		const cacheAppVersion = await CacheStorage.getData( 'cache_app_version' );
		const cachedVersion = _.get( cacheAppVersion, 'data', null );

		if( cachedVersion !== null && cachedVersion !== HelperVersion.get() ) {
			await CacheStorage.clearCache({ restart: false });
			await CacheStorage.storeData( 'cache_app_version', HelperVersion.get() );
			await CacheStorage.saveToDevice();
			appRestart();
			return false;
		}

		if( cachedVersion === null ) {
			await CacheStorage.storeData( 'cache_app_version', HelperVersion.get() );
		}

		return true;
	}
	
	const setLoadedForReal = () => {
		// Background fetch
		GraphqlGroupedBuilder.forceNewBatch();
		
		HelperCache.fetchAll({
			fetchKey: HelperCacheFetchConstants.AFTER
		})
		
		setProgressBarPercentage( 100 )
	}
	
	const fetchStartData = () => {
		// Set the percentage
		setProgressBarPercentage( 90 )
		
		// Check the notifications
		HelperSettingNotifications.validate();
		
		// Set in the background
		( async() => {
			const graphqlBuilder = new GraphqlBuilder();
			
			// Check if we need to set the language (if it is not set)
			const employeeLanguage = _.get( _employeeLanguage, ['code'], null );
			if( employeeLanguage === null ) {
				graphqlBuilder.addQuery( 'employee_language', MutationEmployeeLangauge, {
					language_code: HelperLocale.getDeviceLocale()
				});
			}
			
			const deviceInfo = deviceInformation()
			
			//
			graphqlBuilder.addQuery( 'mutation_employee_version', MutationEmployeeVersion, {
				employee_guid: '::employee_guid',
				device_brand: deviceInfo.deviceBrand,
				device_name: deviceInfo.deviceName,
				platform: deviceInfo.platform,
				platform_version: deviceInfo.platformVersion,
				app_version: deviceInfo.appVersion,
				build_version: deviceInfo.buildVersion,
			});
			
			graphqlBuilder.addQuery( 'filters', QueryFilters, { language_code: _.get( _employeeLanguage, 'code', HelperLocale.getDeviceLocale() ) } )
			
			// AFTER
			graphqlBuilder.setOnSuccess( result => {
				const filters = JSON.parse( _.get( result, ['filters', 'filters', 0, 'filters'], '{none:"true"}' ) );
				dispatch( setData( dataConstants.FILTERS, filters ) )
			});
			
			graphqlBuilder.setOnError( (result, {error}) => {
				if( _.isFunction( onError ) ) {
					onError( error );
				}
			});
			
			graphqlBuilder.execute();
		})()
		
		// Translations are bundled. Refreshing them should not hold up navigation.
		i18n.reloadResources().catch( () => {
		} );
		setLoadedForReal();
	}
	
	useEffect(() => {
		CacheStorage.construct();
		
		getInitialURL().then( result => {
			return dispatch(setData(dataConstants.INITIAL_URL, result));
		} )
		
		HelperLocaleSettings.load().then( () => {
			setProgressBarPercentage( 0 )
			
			if( _.isEmpty( customerId ) ) {
				setLoading( false )
				return
			}
			
			// Constructors for cache
			HelperCache.construct( {
				onConstructed: async () => {
					if( !await validateCacheVersion() ) return;

					// Cached data has been hydrated; let navigation render while
					// freshness checks and network reloads continue in the background.
					setProgressBarPercentage( 100 );
					setLoading( false );

					HelperCache.fetchAll({
						fetchKey: HelperCacheFetchConstants.BEFORE,
						onPercentageUpdate: ( percentage ) => {
							setProgressBarPercentage( percentage )
						},
						onCompleteAll: fetchStartData,
						onError: onError
					})
				}
			});
		});
	}, [] )
	
	return (
		<CacheProviderContext.Provider
			value={{}}>
			
			{ loading ? null : children }
		</CacheProviderContext.Provider>
	)
}

export default CacheProvider
